import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const PendingApprovalsContext = createContext({
  total: 0,
  byModule: {},
  refresh: () => {},
});

const EMPTY = { total: 0, by_module: {} };

/**
 * The pending-approvals count, shared by the sidebar badge and anything else
 * that needs it.
 *
 * **No polling.** A background timer would keep Render's instance awake at cost
 * and still hand you a number stale by up to the interval. Instead this refetches
 * on navigation — which only happens while somebody is actually using the app —
 * and on demand after an approve or reject.
 *
 * If another approver decides something, your badge is briefly high. That is
 * acceptable: the queue itself is authoritative and clicking through corrects it.
 * A badge is a prompt to look, not a source of truth.
 */
export function PendingApprovalsProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [counts, setCounts] = useState(EMPTY);

  const refresh = useCallback(async () => {
    if (!user) {
      setCounts(EMPTY);
      return;
    }
    try {
      const { data } = await api.get("/approvals/count");
      setCounts(data);
    } catch {
      // A badge is not worth an error banner. Leaving the last known count is
      // better than flashing a wrong zero, which reads as "all caught up".
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh, location.pathname]);

  return (
    <PendingApprovalsContext.Provider
      value={{ total: counts.total ?? 0, byModule: counts.by_module ?? {}, refresh }}
    >
      {children}
    </PendingApprovalsContext.Provider>
  );
}

export const usePendingApprovals = () => useContext(PendingApprovalsContext);
